import { Fragment } from "preact";
import { useContext } from "preact/hooks";
import { Text } from "preact-i18n";
import { translate } from "preact-i18n";

import { Configs } from "../../helpers/context";
import { formatIntegerCurrencyString } from "../../helpers/formatters";
import { formatDate } from "../../helpers/formatters/date";
import { FundingSourceType } from "../../enums/fundingSourceType";
import { FeeProgramType } from "../../enums/feeProgramType";
import { capitalizeFirst } from "../../helpers/common";

const OrderSummaryBreakdown = ({ order, subTotal, total, adjustments, tipAmount, payLink }) => {
  const configsContext = useContext(Configs);
  const path = window.location.pathname.split("/");
  let isInvoice;
  let finalAdjustments = adjustments || [];

  if (tipAmount) {
    finalAdjustments = [
      ...finalAdjustments,
      {
        name: translate("CHECKOUT_TIP", "", configsContext?.configs?.messages, {}, undefined, ""),
        value: formatIntegerCurrencyString(
          tipAmount,
          configsContext?.configs?.currency,
          configsContext?.configs?.ecommerceLocaleLanguage,
        ),
      },
    ];
  }

  function sortAdjustments(adjustments) {
    return adjustments.sort((a, b) => {
      // Use the preTax property for sorting
      return a.preTax - b.preTax;
    });
  }

  if (finalAdjustments.length > 0 && finalAdjustments[0].details) {
    finalAdjustments = sortAdjustments(finalAdjustments);
  }

  if ((path[1] === "i" && path[2]) || (path[2] === "i" && path[3])) {
    // invoices at pay.{domain}/i/{invoiceId} OR poynt.net/checkout/i/{invoiceId}
    isInvoice = true;
  } else {
    // pay links otherwise
    isInvoice = false;
  }

  const orderLineItems =
    order && order.order && order.order.value && Array.isArray(order.order.value.lineItems)
      ? order.order.value.lineItems
      : [];

  const sumItemField = (items, field, filterFn) => {
    return items.reduce((total, item) => {
      const values = Array.isArray(item[field]) ? item[field] : [];
      const sum = values.filter(filterFn || (() => true)).reduce((subTotal, entry) => {
        const amount =
          entry && entry.amount && typeof entry.amount.value === "number" ? entry.amount.value : 0;
        return subTotal + amount;
      }, 0);
      return total + sum;
    }, 0);
  };

  const itemLevelFeeSum = sumItemField(orderLineItems, "fees", (fee) => fee.appliedBeforeTax);
  const itemLevelDiscountsSum = sumItemField(
    orderLineItems,
    "discounts",
    (discounts) => discounts.appliedBeforeTax,
  );
  const itemLevelTaxesSum = sumItemField(orderLineItems, "taxes");

  const calculatedSubTotal = subTotal + itemLevelFeeSum + itemLevelTaxesSum - itemLevelDiscountsSum;

  const havePaidPaymentRequests = (payLink?.invoice?.paymentRequests || []).some(
    (paymentRequest) =>
      paymentRequest.status === "CAPTURED" ||
      paymentRequest.status === "AUTHORIZED" ||
      paymentRequest.status === "PAID",
  );

  const getFundingSourceLabel = (fundingSource) => {
    switch (fundingSource?.type) {
      case FundingSourceType.CREDIT_DEBIT:
        return fundingSource?.card
          ? `${capitalizeFirst(fundingSource?.card?.type ?? "")} ****  ${
              fundingSource?.card?.numberLast4
            }`
          : "Card";
      case FundingSourceType.CHEQUE:
        return `${fundingSource.bankAccount?.bankName || "Bank"} ***** ${
          fundingSource?.bankAccount?.accountNumberLastFour || ""
        }`;
      case FundingSourceType.CASH:
        return "Cash";
      case FundingSourceType.CUSTOM_FUNDING_SOURCE:
        return fundingSource?.customFundingSource?.type === "CHEQUE" ? "Check" : "Other";
      default:
        return "Other";
    }
  };

  const getSurchargeAmount = (transaction) => {
    return (
      transaction?.amounts?.transactionFees?.find(
        (fee) => fee.feeProgramType === FeeProgramType.SURCHARGE,
      )?.amount || 0
    );
  };

  return (
    <Fragment>
      {subTotal !== total ? (
        <Fragment>
          <div className="order-subtotal">
            <span style="float:left;">
              <Text id="CHECKOUT_SUBTOTAL">Subtotal</Text>
            </span>
            <span style="float:right;">
              {formatIntegerCurrencyString(
                calculatedSubTotal,
                configsContext?.configs?.currency,
                configsContext?.configs?.ecommerceLocaleLanguage,
              )}
            </span>
            <div style="clear:both;"></div>
          </div>
        </Fragment>
      ) : null}
      {order.notes && !isInvoice ? (
        <Fragment>
          <div className="order-notes">
            {order.notes
              .filter((note) => note.authorType === "CUSTOMER" && note.content.trim() !== "")
              .map((note, index) => (
                <div key={index}>
                  <p>{note.content}</p>
                </div>
              ))}
          </div>
        </Fragment>
      ) : null}
      {finalAdjustments && finalAdjustments.length > 0 ? (
        <div className="order-adjustments">
          {finalAdjustments?.map((adjustment) => {
            const showAdjustment =
              (!("orderLevel" in adjustment) && !("preTax" in adjustment)) ||
              adjustment.orderLevel ||
              (adjustment.preTax === 3 && !adjustment.orderLevel);
            if (showAdjustment) {
              return (
                <div className="order-adjustment-item">
                  <div className="order-adjustment-item-left">
                    <div className="order-adjustment-item-title">
                      <span style="float:left;">
                        <Text id="adjustment-example-title">{adjustment.name}</Text>
                      </span>
                      {adjustment.details ? (
                        <Fragment>
                          <br />
                          <div className="order-adjustment-item-desc">
                            <span style="float:left;">
                              <Text id="adjustment-example-desc">{adjustment.details}</Text>
                            </span>
                          </div>
                        </Fragment>
                      ) : null}
                    </div>
                  </div>
                  <div order-adjustment-item-right>
                    <span className="order-adjustment-item-value" style="float:right;">
                      {adjustment.value}
                    </span>
                  </div>
                </div>
              );
            }
          })}
        </div>
      ) : null}
      {havePaidPaymentRequests ? (
        <Fragment>
          <div className="order-summary-divider"></div>
          <div className="invoice-payment-request-total">
            <div className="invoice-payment-request-total-amount">
              <span style="float:left">
                <Text id="CHECKOUT_INVOICE_TOTAL">Invoice Total</Text>
              </span>
              <span style="float:right">
                {formatIntegerCurrencyString(
                  total,
                  configsContext?.configs?.currency,
                  configsContext?.configs?.ecommerceLocaleLanguage,
                )}
              </span>
              <div style="clear:both;"></div>
            </div>
            {(payLink.transactions || []).map((txn) => (
              <div className="order-amounts">
                <span style="float:left">
                  <Text id="CHECKOUT_PAYMENT">Payment</Text>
                  <span className="order-amounts-ml-xs">
                    ({getFundingSourceLabel(txn.fundingSource)})
                  </span>
                  <span className="order-amounts-sub-text">
                    {formatDate(txn.createdAt, configsContext?.configs?.ecommerceLocaleLanguage, {
                      year: "numeric",
                      month: "short",
                      day: "2-digit",
                    })}
                  </span>
                </span>
                <span style="float:right;text-align:right">
                  {formatIntegerCurrencyString(
                    txn.amounts.orderAmount,
                    configsContext?.configs?.currency,
                    configsContext?.configs?.ecommerceLocaleLanguage,
                  )}
                  {txn.amounts.tipAmount > 0 && (
                    <div className="order-amounts-bottom-sub-text">
                      +
                      {formatIntegerCurrencyString(
                        txn.amounts.tipAmount,
                        configsContext?.configs?.currency,
                        configsContext?.configs?.ecommerceLocaleLanguage,
                      )}{" "}
                      <Text id="CHECKOUT_TIP">Tip</Text>
                    </div>
                  )}
                  {getSurchargeAmount(txn) > 0 && (
                    <div className="order-amounts-bottom-sub-text">
                      +
                      {formatIntegerCurrencyString(
                        getSurchargeAmount(txn),
                        configsContext?.configs?.currency,
                        configsContext?.configs?.ecommerceLocaleLanguage,
                      )}{" "}
                      <Text id="CHECKOUT_SURCHARGE_FEE">Surcharge Fee</Text>
                    </div>
                  )}
                </span>
                <div style="clear:both;"></div>
              </div>
            ))}
          </div>
          <div className="order-summary-divider"></div>
          <div className="order-total">
            <span style="float:left">
              <Text id="CHECKOUT_TOTAL_DUE">Total Due</Text>
            </span>
            <span style="float:right;">
              {formatIntegerCurrencyString(
                payLink.remainingAmount,
                configsContext?.configs?.currency,
                configsContext?.configs?.ecommerceLocaleLanguage,
              )}
            </span>
            <div style="clear:both;"></div>
          </div>
        </Fragment>
      ) : (
        <div className="order-total">
          <span style="float:left">
            <Text id="CHECKOUT_TOTAL">Total</Text>
          </span>
          <span style="float:right;">
            {formatIntegerCurrencyString(
              total,
              configsContext?.configs?.currency,
              configsContext?.configs?.ecommerceLocaleLanguage,
            )}
          </span>
          <div style="clear:both;"></div>
        </div>
      )}
    </Fragment>
  );
};

export default OrderSummaryBreakdown;
